import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Button from "../atoms/Button";
import { Facebook, Linkedin, Instagram } from "lucide-react";

import { useJobStore } from "../../store/jobStore";
import SectionBox from "../layout/SectionBox";
import JobSearchDetail from "./JobSearchDetail";
import ApplyJobModal from "../modals/ApplyJobModal";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

export default function JobDetailLayout() {
    const { jobs, fetchJobs, filters, 
    page, hasMore, loading } = useJobStore();
    const [selectedJob, setSelectedJob] = useState<any>(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const { slug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [showApplyModal, setShowApplyModal] = useState(false);

    const detailRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const lang = location.pathname.startsWith("/id") ? "id" : "en";

    // 🔵 SLUGIFY FIX
    function slugify(text: string = "") {
        return text
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
    }

    const jobId = Number(slug);

    // 🔵 FETCH DATA
    useEffect(() => {
        useJobStore.setState({
      page: 1,
      jobs: [],
    });
        fetchJobs(lang);
    }, [lang, filters, fetchJobs]);

    useEffect(() => {
        setSelectedJob(null);
    }, [filters]);

    const activeJob = useMemo(() => {
        if (!jobId || jobs.length === 0) return null;

        return jobs.find((job) => job.id === jobId) || null;
    }, [jobId, jobs]);
    const orderedJobs = useMemo(() => {
        return jobs;
    }, [jobs]);


    // 🔵 SET SELECTED JOB
    useEffect(() => {
        if (activeJob) setSelectedJob(activeJob);
    }, [activeJob]);

    // 🔵 Scroll top
    useLayoutEffect(() => {
        if (detailRef.current) detailRef.current.scrollTop = 0;
    }, [selectedJob]);

    const handleSelectJob = (job: any) => {
        setSelectedJob(job);
        navigate(`/${lang}/jobs/${job.id}`);
    };


    const decodeHtml = (htmlString: string) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = htmlString;
        return txt.value;
    };

    function extractIframeSrc(htmlString: string): string | null {
        const match = htmlString.match(/src="([^"]+)"/);
        return match ? match[1] : null;
    }

      const handleLoadMore = async () => {
  await fetchJobs(lang, true);
};



    return (
        <section className="w-full md:pt-6 mt-24 relative container-section mx-auto">
            <JobSearchDetail />

            <div className="mx-auto md:mt-8 pt-8 md:pt-0">
                <SectionBox classNameSub="!mb-0" background="linear-gradient(90deg, #2c5a7f 0%, #a7bfd3 30%, #ffffff 70%, #ffffff 100%)">
                    <div className="mb-3 md:hidden flex justify-start">
                        <button
                            onClick={() => navigate(`/${lang}/jobs`)}
                            className="
                                px-6 py-2
                                rounded-full
                                border border-[#123b7c]
                                bg-[#123b7c]
                                text-white
                                font-medium
                                transition
                            "
                        >
                            {lang === "id" ? "< Kembali ke Daftar Pekerjaan" : "< Back to Jobs List"}
                        </button>
                    </div>
                    <div className="flex flex-col-reverse lg:grid lg:grid-cols-[35%_65%] gap-6">

                        {/* 🟦 LEFT LIST */}
                        <div
                            ref={listRef}
                            className="hidden md:flex flex-col gap-4 max-h-[80vh] overflow-y-auto pr-2 transition-all"
                        >
                            {orderedJobs.map((job, index) => {
                                // const isActive =
                                //     slugify(job.title) === slugify(slug || "");

                                const isActive = job.id === Number(slug);

                                return (
                                    <div
                                        key={index}
                                        onClick={() => handleSelectJob(job)}
                                        className={`cursor-pointer border rounded-lg p-4 shadow-lg transition-all relative ${isActive
                                            ? "border-[#123b7c] bg-primary"
                                            : "border-gray-200 hover:border-[#123b7c]/50"
                                            }`}
                                    >
                                        {/* Company Image */}
                                        {job.company_logo && (
                                            <img
                                                src={job.company_logo}
                                                alt={job.company_logo}
                                                className="absolute top-4 right-4 w-12 h-12 rounded-md object-cover border border-white/20"
                                            />
                                        )}

                                        {/* TITLE */}
                                        <h4
                                            className={`font-semibold text-sm pr-16 ${isActive ? "text-white" : "text-white/80"
                                                }`}
                                        >
                                            {job.title}
                                        </h4>

                                        {/* META */}
                                        <div className="mt-3 space-y-1">
                                            <p className={`text-xs ${isActive ? "text-white" : "text-white/80"}`}>
                                                {job.company}
                                            </p>
                                            <p className={`text-xs ${isActive ? "text-white" : "text-white/80"}`}>
                                                {job.priceRange}
                                            </p>
                                            <p className={`text-xs ${isActive ? "text-white" : "text-white/80"}`}>
                                                {job.education}
                                            </p>
                                            <p className={`text-xs ${isActive ? "text-white" : "text-white/80"}`}>
                                                {job.location}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                            {hasMore ? (
                                <Button
                                                onClick={handleLoadMore}
                                                bgClass="bg-[#123b7c]"
                                                textClass="text-white"
                                                className="px-8 py-3 rounded-full font-semibold"
                                                disabled={loading}
                                              >
                                                {loading ? "Loading..." : "Load More"}
                                              </Button>
                            ) : (
                                <p className="text-center text-sm text-white py-4">
                                    No more jobs available
                                </p>
                            )}

                        </div>

                        <div className="relative bg-white border border-gray-200 rounded-lg shadow-sm max-h-[80vh] flex flex-col">

                            <div
                                ref={detailRef}
                                className="flex-1 overflow-y-auto p-4 md:p-6"
                            >
                                {selectedJob ? (
                                    <>
                                        <h2 className="text-2xl font-semibold text-primary">
                                            {selectedJob.title}
                                        </h2>
                                        <div className="flex items-center gap-2 my-2">
                                            <img className="w-12 h-12 rounded-md object-cover" src={selectedJob.company_logo}></img>
                                            <p className="text-gray-600 mb-1">{selectedJob.company}</p>

                                        </div>
                                        <p className="text-sm text-gray-500 mb-4">{selectedJob.location}</p>

                                        <div className="flex flex-wrap gap-2 mb-6 text-sm">
                                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                                                {selectedJob.yearExperience}
                                            </span>
                                            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                                                {selectedJob.education}
                                            </span>
                                        </div>

                                        {/* <button
                                        onClick={() => setShowApplyModal(true)}
                                        className="bg-[#123b7c] text-white px-6 py-2 rounded-full font-medium hover:bg-[#0e2f66] mb-6"
                                    >
                                        {lang === "id" ? "Lamar Sekarang" : "Apply Now"}
                                    </button> */}




                                        <h3 className="text-xl font-bold mb-4">Job Description</h3>

                                        {/* DESCRIPTION */}
                                        <div>
                                            <h3 className="text-xl font-semibold mb-2">Responsibilities</h3>
                                            <div
                                                className="text-gray-700 leading-relaxed text-md [&_ul]:list-disc [&_ul]:ms-5 [&_ol]:list-decimal [&_ol]:ms-5 [&_p]:my-2"
                                                dangerouslySetInnerHTML={{
                                                    __html: selectedJob.description || "-",
                                                }}
                                            />
                                        </div>

                                        {/* REQUIREMENT */}
                                        <div className="mt-8">
                                            <h3 className="text-xl font-semibold mb-2">Requirements</h3>
                                            <div
                                                className="text-gray-700 leading-relaxed text-md [&_ul]:list-disc [&_ul]:ms-5 [&_ol]:list-decimal [&_ol]:ms-5 [&_p]:my-2"
                                                dangerouslySetInnerHTML={{
                                                    __html: selectedJob.requirement || "-",
                                                }}
                                            />
                                        </div>

                                        {/* SKILLS */}
                                        <div>
                                            <hr className="my-4" />
                                            <p className="text-gray-700 leading-relaxed text-md mt-4">
                                                Key Skills
                                                <div className="my-4 pb-4 flex gap-4 overflow-x-auto whitespace-nowrap">
                                                    {selectedJob.skill?.map((s: string, i: number) => (
                                                        <p
                                                            key={i}
                                                            className="text-sm bg-blue-100 p-2 rounded-md inline-block"
                                                        >
                                                            {s}
                                                        </p>
                                                    ))}
                                                </div>
                                            </p>
                                        </div>

                                        {/* BENEFIT */}
                                        <div>
                                            <hr className="my-4" />
                                            <p className="text-gray-700 leading-relaxed text-md mt-4">
                                                Benefits
                                            </p>

                                            <div className="my-4 pb-4 flex gap-4 overflow-x-auto whitespace-nowrap">

                                                {selectedJob.benefit?.map((s: string, i: number) => (
                                                    <p
                                                        key={i}
                                                        className="text-sm bg-blue-100 p-2 rounded-md inline-block"
                                                    >
                                                        {s}
                                                    </p>
                                                ))}
                                            </div>

                                        </div>

                                        {/* COMPANY INFO */}
                                        <div>
                                            <hr className="my-4" />
                                            <p className="text-gray-700 leading-relaxed text-md mt-4">
                                                About The Company
                                                <div className="my-4 flex gap-4 md:hidden">
                                                    {selectedJob.company_logo && (
                                                        <img
                                                            src={selectedJob.company_logo}
                                                            alt={selectedJob.company}
                                                            className="w-16 h-16 rounded-md object-cover border border-white/20"
                                                        />
                                                    )}

                                                    <div>
                                                        <h4 className="font-semibold text-sm">{selectedJob.company}</h4>
                                                        <h4 className="font-thin text-sm">{selectedJob.industry} </h4>
                                                        <h4 className="font-thin text-sm">{selectedJob.size}</h4>
                                                        


                                                        <div className="flex gap-4 items-center mt-2">
                                                            {selectedJob.company_instagram && (
                                                                <a
                                                                    href={selectedJob.company_instagram}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label="Instagram"
                                                                >
                                                                    <Instagram className="w-5 h-5  cursor-pointer" />
                                                                </a>
                                                            )}

                                                            {selectedJob.company_facebook && (
                                                                <a
                                                                    href={selectedJob.company_facebook}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label="Facebook"
                                                                >
                                                                    <Facebook className="w-5 h-5 cursor-pointer" />
                                                                </a>
                                                            )}

                                                            {selectedJob.company_linkedin && (
                                                                <a
                                                                    href={selectedJob.company_linkedin}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label="LinkedIn"
                                                                >
                                                                    <Linkedin className="w-5 h-5 cursor-pointer" />
                                                                </a>
                                                            )}

                                                            {selectedJob.company_tiktok && (
                                                                <a
                                                                    href={selectedJob.company_tiktok}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label="TikTok"
                                                                >
                                                                    <img
                                                                        className="w-6 h-6 hover:opacity-80 transition cursor-pointer"
                                                                        src="https://static.vecteezy.com/system/resources/thumbnails/018/930/574/small/tiktok-logo-tikok-icon-transparent-tikok-app-logo-free-png.png"
                                                                        alt="TikTok"
                                                                    />
                                                                </a>
                                                            )}
                                                        </div>
                                                        <p className="text-sm mt-2">Office Address</p>
                                                        <p className="font-thin text-sm">
                                                            {selectedJob.company_address}
                                                        </p>
                                                        {selectedJob?.map && (
                                                            (() => {
                                                                const decodedHtml = decodeHtml(selectedJob.map);

                                                                // Ambil hanya iframe src
                                                                const iframeSrc = extractIframeSrc(decodedHtml);

                                                                return (
                                                                    <>
                                                                        {/* PREVIEW IFRAME CLEAN VERSION */}
                                                                        {iframeSrc ? (
                                                                            <iframe
                                                                                src={iframeSrc}
                                                                                width="100%"
                                                                                height="300"
                                                                                style={{ border: 0 }}
                                                                                loading="lazy"
                                                                                allowFullScreen
                                                                                referrerPolicy="no-referrer-when-downgrade"
                                                                                className="rounded-lg hidden md:block overflow-hidden border mt-4"
                                                                            />
                                                                        ) : (
                                                                            <p className="text-red-500 mt-2">Invalid iframe embed</p>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()
                                                        )}




                                                    </div>
                                                </div>

                                                <div className="hidden md:block">
                                                    {/* HEADER INFO */}
                                                    <div className="my-4 flex justify-between">
                                                        <div className="flex gap-4">
                                                        {selectedJob.company_logo && (
                                                            <img
                                                                src={selectedJob.company_logo}
                                                                alt={selectedJob.company}
                                                                className="w-16 h-16 rounded-md object-cover border border-white/20"
                                                            />
                                                        )}

                                                        <div>
                                                            <h4 className="font-semibold text-sm">{selectedJob.company}</h4>
                                                            <h4 className="font-thin text-sm">{selectedJob.industry}</h4>
                                                            <h4 className="font-thin text-sm">{selectedJob.size}</h4>

                                                            

                                                        <div className="flex gap-4 items-center mt-2">
                                                            {selectedJob.company_instagram && (
                                                                <a
                                                                    href={selectedJob.company_instagram}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label="Instagram"
                                                                >
                                                                    <Instagram className="w-5 h-5 cursor-pointer" />
                                                                </a>
                                                            )}

                                                            {selectedJob.company_facebook && (
                                                                <a
                                                                    href={selectedJob.company_facebook}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label="Facebook"
                                                                >
                                                                    <Facebook className="w-5 h-5 cursor-pointer" />
                                                                </a>
                                                            )}

                                                            {selectedJob.company_linkedin && (
                                                                <a
                                                                    href={selectedJob.company_linkedin}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label="LinkedIn"
                                                                >
                                                                    <Linkedin className="w-5 h-5 cursor-pointer" />
                                                                </a>
                                                            )}

                                                            {selectedJob.company_tiktok && (
                                                                <a
                                                                    href={selectedJob.company_tiktok}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label="TikTok"
                                                                >
                                                                    <img
                                                                        className="w-6 h-6 hover:opacity-80 transition cursor-pointer"
                                                                        src="https://static.vecteezy.com/system/resources/thumbnails/018/930/574/small/tiktok-logo-tikok-icon-transparent-tikok-app-logo-free-png.png"
                                                                        alt="TikTok"
                                                                    />
                                                                </a>
                                                            )}
                                                        </div>

                                                            <p className="text-sm mt-2">Office Address</p>
                                                            <p className="font-thin text-sm">
                                                                {selectedJob.company_address}
                                                            </p>
                                                        </div>

                                                        </div>
                                                    </div>

                                                    {/* FULL WIDTH MAP */}
                                                    {selectedJob?.map && (
                                                        (() => {
                                                            const decodedHtml = decodeHtml(selectedJob.map);
                                                            const iframeSrc = extractIframeSrc(decodedHtml);

                                                            return iframeSrc ? (
                                                                <div className="mt-4 hidden md:block w-full">
                                                                    <iframe
                                                                        src={iframeSrc}
                                                                        width="100%"
                                                                        height="300"
                                                                        className="rounded-lg hidden md:block border"
                                                                        style={{ border: 0 }}
                                                                        loading="lazy"
                                                                        allowFullScreen
                                                                        referrerPolicy="no-referrer-when-downgrade"
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <p className="text-red-500 mt-2">Invalid iframe embed</p>
                                                            );
                                                        })()
                                                    )}
                                                </div>

                                                
                                                    {selectedJob?.map && (
                                                        (() => {
                                                            const decodedHtml = decodeHtml(selectedJob.map);
                                                            const iframeSrc = extractIframeSrc(decodedHtml);

                                                            return iframeSrc ? (
                                                                <div className="mt-4  w-full">
                                                                    <iframe
                                                                        src={iframeSrc}
                                                                        width="100%"
                                                                        height="300"
                                                                        className="rounded-lg block md:hidden border"
                                                                        style={{ border: 0 }}
                                                                        loading="lazy"
                                                                        allowFullScreen
                                                                        referrerPolicy="no-referrer-when-downgrade"
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <p className="text-red-500 mt-2">Invalid iframe embed</p>
                                                            );
                                                        })()
                                                    )}


                                                <p className="text-gray-700 leading-relaxed text-md hidden md:block mt-4">
                                                    Gallery Company
                                                </p>
                                                {selectedJob?.galleyImage?.length > 0 ? (
                                                    <div className="mt-4 w-full hidden md:block flex justify-center">
                                                        <Swiper
                                                            modules={[Pagination, Navigation]}
                                                            spaceBetween={16}
                                                            slidesPerView={1}
                                                            navigation
                                                            pagination={{ clickable: true }}
                                                            className="max-w-[800px]" // maksimal lebar container Swiper
                                                        >
                                                            {selectedJob.galleyImage.map((img: string, idx: number) => (
                                                                <SwiperSlide key={idx} className="flex justify-center">
                                                                    <img
                                                                        src={img}
                                                                        alt={`Gallery ${idx}`}
                                                                        className="w-full max-w-[450px] max-h-[300px] object-cover rounded-lg cursor-pointer mx-auto"
                                                                        onClick={() => {
                                                                            setLightboxIndex(idx);
                                                                            setLightboxOpen(true);
                                                                        }}
                                                                    />
                                                                </SwiperSlide>
                                                            ))}
                                                        </Swiper>
                                                    </div>

                                                ) : (
                                                    <div className="mt-4 bg-secondary p-4 rounded-lg text-yellow-700 text-center">
                                                        ⚠️ No gallery images available
                                                    </div>
                                                )}


                                                <Lightbox
                                                    open={lightboxOpen}
                                                    index={lightboxIndex}
                                                    close={() => setLightboxOpen(false)}
                                                    slides={selectedJob?.galleyImage?.map((src: any) => ({ src })) || []}
                                                    plugins={[Zoom]}
                                                />
                                            </p>
                                        </div>

                                    </>
                                ) : (
                                    <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
                                        <p className="text-gray-500 text-lg font-medium">
                                            No data found
                                        </p>
                                        <p className="text-sm text-gray-400 mt-1">
                                            Try adjusting your search or filter
                                        </p>
                                    </div>
                                )}
                                {selectedJob && (
                                    <div className="sticky bottom-0 bg-transparent p-4 flex justify-end w-fit z-40 place-self-center md:place-self-end">
                                    <button
                                        onClick={() => setShowApplyModal(true)}
                                        className="
                                        bg-[#123b7c]
                                        text-white
                                        px-10 py-4
                                        rounded-full
                                        font-bold
                                        text-lg
                                        shadow-xl
                                        hover:scale-105
                                        transition
                                        "
                                    >
                                        🚀 {lang === "id" ? "Lamar Sekarang" : "Apply Now"}
                                    </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* 🟦 DETAIL KANAN */}
                    </div>
                </SectionBox>
                <ApplyJobModal
                    isOpen={showApplyModal}
                    onClose={() => setShowApplyModal(false)}
                    job={selectedJob}
                    lang={lang}
                />

            </div>
        </section>
    );
}
